import React, { useMemo } from 'react';
import { globalMenuItems } from '../../App';
import { useExposure } from '../../hooks/useExposure';
import { useMaterials } from '../../hooks/useMaterials';
import { useUser } from '../../hooks/useUser';
import Sidebar from './Sidebar/sidebar';
import TopBar from './TopBar/TopBar';
import { useAccountStore } from '../Stores/accountStore';
import { Account } from '../../sp-types/accountTypes';
import { FormulaCardBar } from '../FormulaCardBar/FormulaCardBar';
import { useCategoryTerms } from '../../utils/categoryTerms';

type TopSideBarProps = {
  children?: React.ReactNode;
  currentItem: string;
  hideFormulaBar?: boolean;
};

// Map from default route names to custom terms for sidebar display
const CATEGORY_NAME_MAP: Record<string, 'demand' | 'supply' | 'hedge'> = {
  Demand: 'demand',
  Supply: 'supply',
  Hedges: 'hedge',
};

const TopSideBars: React.FC<TopSideBarProps> = ({ children, currentItem, hideFormulaBar }) => {
  const { data: userData } = useUser();
  const { account } = useAccountStore();
  const terms = useCategoryTerms();

  // The hooks are here to initialize the data
  // eslint-disable-next-line no-empty-pattern
  const {} = useExposure();
  // eslint-disable-next-line no-empty-pattern
  const {} = useMaterials();

  const additional = userData?.menuItem || [];
  // Global superadmin flag is the authoritative source for superadmin-only items
  const isSuperadmin = !!userData?.isSuperadmin;
  // Admin includes either account/global admins or superadmins
  const isAdmin = !!(userData?.isAdmin || isSuperadmin);
  const filterMenu = (items: any[], account: Account) => {
    return account.isSalesCommitment
      ? items.filter((m) => !m.isSalesCommitment || m.isSalesCommitment === 1)
      : items.filter((m) => !m.isSalesCommitment || m.isSalesCommitment === 2);
  };

  // Replace default category names with custom terms for sidebar display
  const menuItems = useMemo(() => {
    const filtered = filterMenu(globalMenuItems, account);
    return filtered.map((item: any) => {
      const termKey = CATEGORY_NAME_MAP[item.name];
      return termKey ? { ...item, matchKey: item.name, name: terms[termKey] } : item;
    });
  }, [account, terms]);

  return (
    <div className="flex h-screen overflow-hidden ">
      <div className="2xl:w-56 flex-shrink-0 bg-primary_text overflow-y-scroll ">
        <Sidebar
          items={[...menuItems, ...additional]}
          currentItem={currentItem}
          isAdmin={isAdmin}
          isSuperadmin={isSuperadmin}
        />
      </div>
      <div className="flex flex-col flex-1 min-w-0 max-h-screen">
        <TopBar currentPage={currentItem} />
        {!hideFormulaBar && (
          <div className="px-3 pt-3">
            <FormulaCardBar />
          </div>
        )}
        <div className="flex-1 overflow-auto p-3">{children}</div>
      </div>
    </div>
  );
};

export default TopSideBars;
